import React from 'react';
import { buttonVariants } from '@/components/ui/button';
import { Link as GatsbyLink } from 'gatsby';
import type { InternalLinkInterface } from '@/types';
import { urlBuilder } from '@/utils';

import { cn } from '@/lib/utils';

interface Props {
  internalLink?: InternalLinkInterface;
  externalLink?: string;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'linkInline'
    | 'icon'
    | null
    | undefined;
  size?: 'default' | 'sm' | 'lg' | 'icon' | null | undefined;
  className?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}

function Link({
  internalLink,
  externalLink,
  variant,
  size,
  className,
  children,
  ariaLabel = '',
}: Props) {
  // Determine if the inner wrapper should be used
  const useInnerWrapper = variant === 'default' || variant === 'outline';

  // Render the inner wrapper if the variant is 'default' or 'outline'
  const content = useInnerWrapper ? (
    <>
      <div
        className={`${variant === 'outline' ? 'border border-current bg-transparent' : 'bg-yellow-500'} ease-bounce absolute inset-0 flex items-center overflow-hidden rounded-full transition-all after:absolute after:block after:w-full after:scale-0 after:rounded-full after:bg-blue-950 after:pt-[100%] after:opacity-20 after:transition-all group-hover:scale-[1.05] group-hover:after:scale-100 group-hover:after:opacity-100 group-focus:scale-[1.05] group-focus:after:scale-100 group-focus:after:opacity-100 group-active:scale-[1.05] group-active:after:scale-100 group-active:after:opacity-100 md:group-hover:scale-[1.15] md:group-focus:scale-[1.15] md:group-active:scale-[1.15]`}
      ></div>
      <span className='relative'>{children}</span>
    </>
  ) : (
    children
  );

  const buttonVariantsClasses = cn(buttonVariants({ variant, size, className }));
  const classes = !variant && !size ? className : buttonVariantsClasses;

  if (internalLink) {
    return (
      <GatsbyLink
        to={urlBuilder(internalLink)}
        className={classes}
        {...(ariaLabel && { 'aria-label': ariaLabel })}
      >
        {content}
      </GatsbyLink>
    );
  } else if (externalLink) {
    return (
      <a
        href={externalLink}
        className={classes}
        target='_blank'
        rel='noreferrer'
        {...(ariaLabel && { 'aria-label': ariaLabel })}
      >
        {content}
      </a>
    );
  }
}

export default Link;
